import { forwardRef, memo, type ComponentProps, type ReactNode } from 'react'
import { ChevronsDownUp, SlidersHorizontal, type LucideIcon } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Button, Popover, Tooltip } from '@highlight/ui'

/**
 * A heading over a run of rows on the To-dos Home, which has no section chrome:
 * headings alone divide Upcoming from each group of to-dos.
 *
 * `options` is the body of a popover behind a sliders button at the heading's right edge. `action`
 * sits just before it; fill it with a `HomeHeadingAction` or `HomeHeadingCollapse`.
 */
export const HomeHeading = memo(function HomeHeading({
  children,
  count,
  action,
  options,
}: {
  children: string
  count?: string
  action?: ReactNode
  options?: ReactNode
}) {
  // Set apart by weight and by sitting closer to its rows than to the run above.
  return (
    <div className="flex items-center gap-1.5 px-3 pb-1.5 pt-4">
      <h2 className="text-foreground-secondary type-ui-md flex items-baseline gap-1.5 font-medium">
        {children}
        {count && <span className="text-foreground-placeholder type-ui-sm font-normal tabular-nums">{count}</span>}
      </h2>
      {/* Pulled into the heading's line height so the buttons don't make it taller, and out by an
          icon's inset in its 20px box so the last icon ends on the rows' right edge. */}
      <div className="-my-1 -mr-[3px] ml-auto flex items-center gap-0.5">
        {action}
        {options && <HomeHeadingOptions label={children}>{options}</HomeHeadingOptions>}
      </div>
    </div>
  )
})

interface HomeHeadingActionProps extends Omit<ComponentProps<typeof Button>, 'children' | 'left'> {
  icon?: LucideIcon
  /** Unset, the button is the icon alone and `aria-label` names it. */
  children?: string
}

/** A button on the right of a `HomeHeading`, set in the heading's own type. */
export const HomeHeadingAction = forwardRef<HTMLButtonElement, HomeHeadingActionProps>(function HomeHeadingAction(
  { children, icon: Icon, className, ...props },
  ref,
) {
  // Sized to the 14px text rather than the xs button's 12px glyph.
  const icon = Icon && <Icon aria-hidden="true" className="size-3.5" />
  return (
    <Button
      ref={ref}
      type="button"
      variant="ghost"
      size="xs"
      shape={children ? undefined : 'square'}
      left={children ? icon : undefined}
      // `text-md` is 14px at `type-ui-md`'s leading. twMerge swaps it for the xs `text-xs`, which it wouldn't do for `type-ui-md`.
      className={twMerge('text-foreground-secondary hover:text-foreground text-md font-medium', className)}
      {...props}
    >
      {children ?? icon}
    </Button>
  )
})

/** Folds an expanded run back to its first page. */
export function HomeHeadingCollapse({ section, onClick }: { section: string; onClick: () => void }) {
  return (
    <Tooltip>
      <Tooltip.Trigger
        render={<HomeHeadingAction icon={ChevronsDownUp} aria-label={`Collapse ${section}`} onClick={onClick} />}
      />
      <Tooltip.Content>Collapse</Tooltip.Content>
    </Tooltip>
  )
}

function HomeHeadingOptions({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Popover>
      <Popover.Trigger render={<HomeHeadingAction icon={SlidersHorizontal} aria-label={`${label} options`} />} />
      <Popover.Content side="bottom" align="end" positionerClassName="z-[80]" className="w-64 p-1">
        {children}
      </Popover.Content>
    </Popover>
  )
}
